import React, { useState, useEffect } from "react";
import { CodeExample } from "components/CodeExample";
import {
  MagnifyingGlassIcon,
  PencilSquareIcon,
  RocketLaunchIcon,
  LockClosedIcon,
  DocumentTextIcon,
  BoltIcon,
  LightBulbIcon,
  EnvelopeIcon,
  BookOpenIcon
} from "components/Icons";

const IndexPage = (props) => {
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setDarkMode(document.body.classList.contains("dark-mode"));
    };

    // Check initial state
    checkDarkMode();

    // Watch for changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="page">
      <section>
        <div className="section-text">
          <h1 className="main-heading" style={{ fontSize: "3.2rem", fontWeight: "700", marginBottom: "16px" }}>
            Build Amazing Veterinary Software with Vetspire API
          </h1>
          <p className="main-description" style={{ fontSize: "1.8rem", marginBottom: "24px", lineHeight: "1.6" }}>
            The modern GraphQL API for veterinary practice management. Connect your applications
            to client records, patient data, appointments, billing, and more with type-safe,
            flexible queries that return exactly the data you need.
          </p>

          <div className="intro-card intro-special" style={{
            backgroundColor: darkMode ? "#21262d" : "#f7fafc",
            border: `1px solid ${darkMode ? "#30363d" : "#e2e8f0"}`,
            borderRadius: "8px",
            padding: "20px",
            marginBottom: "24px"
          }}>
            <h3 style={{ marginTop: "0", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
              <RocketLaunchIcon /> What makes Vetspire API special?
            </h3>
            <ul style={{ marginBottom: "0", paddingLeft: "20px" }}>
              <li><strong>GraphQL-first:</strong> Fetch exactly what you need in a single request</li>
              <li><strong>Veterinary-focused:</strong> Purpose-built for animal healthcare workflows</li>
              <li><strong>Real-time:</strong> Support for subscriptions and live data updates</li>
              <li><strong>Type-safe:</strong> Complete TypeScript definitions and schema introspection</li>
            </ul>
          </div>

          <p>
            Built on the powerful{" "}
            <a href="http://graphql.org/" target="_blank" rel="noopener noreferrer">GraphQL specification</a>,
            our API gives you the flexibility to request precisely the data your application needs.
            Whether you're building a mobile app, web dashboard, or integrating with existing
            systems, Vetspire API adapts to your requirements.
          </p>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "16px",
            marginTop: "24px",
            marginBottom: "24px"
          }}>
            <div className="intro-card" style={{
              border: `1px solid ${darkMode ? "#30363d" : "#e2e8f0"}`,
              borderRadius: "8px",
              padding: "16px",
              backgroundColor: darkMode ? "#161b22" : "#ffffff"
            }}>
              <div style={{ color: "#10b981", marginBottom: "8px", display: "flex" }}>
                <LockClosedIcon />
              </div>
              <h4 style={{ margin: "0 0 8px 0" }}>Secure by Design</h4>
              <p style={{ margin: "0", fontSize: "1.4rem" }}>
                Enterprise-grade security with API key authentication and environment isolation.
              </p>
            </div>
            <div className="intro-card" style={{
              border: `1px solid ${darkMode ? "#30363d" : "#e2e8f0"}`,
              borderRadius: "8px",
              padding: "16px",
              backgroundColor: darkMode ? "#161b22" : "#ffffff"
            }}>
              <div style={{ color: "#f59e0b", marginBottom: "8px", display: "flex" }}>
                <DocumentTextIcon />
              </div>
              <h4 style={{ margin: "0 0 8px 0" }}>Rich Documentation</h4>
              <p style={{ margin: "0", fontSize: "1.4rem" }}>
                Interactive examples, comprehensive type definitions, and real-world use cases.
              </p>
            </div>
            <div className="intro-card" style={{
              border: `1px solid ${darkMode ? "#30363d" : "#e2e8f0"}`,
              borderRadius: "8px",
              padding: "16px",
              backgroundColor: darkMode ? "#161b22" : "#ffffff"
            }}>
              <div style={{ color: "#8b5cf6", marginBottom: "8px", display: "flex" }}>
                <BoltIcon />
              </div>
              <h4 style={{ margin: "0 0 8px 0" }}>Developer Experience</h4>
              <p style={{ margin: "0", fontSize: "1.4rem" }}>
                Multiple environments, helpful error messages, and extensive tooling support.
              </p>
            </div>
          </div>

          <p className="important">
            Stay informed about API updates by subscribing to our{" "}
            <a href="https://groups.google.com/forum/#!forum/vetspire-api-announce">
              developer announcement mailing list
            </a>
            . We'll notify you about new features, breaking changes, and best practices.
          </p>

          <h3>Get Started</h3>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "16px" }}>
            <a href="https://groups.google.com/forum/#!forum/vetspire-api-announce"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "12px 20px",
                backgroundColor: "#4d65ff",
                color: "white",
                borderRadius: "6px",
                textDecoration: "none",
                fontWeight: "600",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#3a4dd4"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "#4d65ff"}>
              <EnvelopeIcon /> Subscribe to Updates
            </a>
            <a href="http://graphql.org/learn/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "12px 20px",
                backgroundColor: "white",
                color: "#4d65ff",
                borderRadius: "6px",
                textDecoration: "none",
                fontWeight: "600",
                border: "2px solid #4d65ff",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#4d65ff";
                e.target.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "white";
                e.target.style.color = "#4d65ff";
              }}>
              <BookOpenIcon /> Learn GraphQL
            </a>
          </div>
        </div>
        <div className="section-example on">
          <div className="section-example-area">
            <div style={{ marginBottom: "8px", fontSize: "1.4rem", fontWeight: "600" }}>
              API <strong>Production</strong> Endpoint
            </div>
            <pre>https://api.vetspire.com/graphql</pre>
          </div>
          <div className="section-example-area">
            <div style={{ marginBottom: "8px", fontSize: "1.4rem", fontWeight: "600" }}>
              API <strong>Testing</strong> Endpoint
            </div>
            <pre>https://api.staging.vetspire.com/graphql</pre>
          </div>
          <div className="section-example-area">
            <div style={{ marginBottom: "8px", fontSize: "1.4rem", fontWeight: "600" }}>
              API <strong>Sandbox</strong> Endpoint
            </div>
            <pre>https://api.sandbox.vetspire.com/graphql</pre>
          </div>
        </div>
      </section>
      <section>
        <div className="section-text">
          <h2>Authentication</h2>
          <p>
            Every request made to our API Endpoint must include an Authorization
            header which includes your secret API Key. Your secret API Key enables
            read and write access to your entire organization's data, so keep it
            safe and secret! It should be treated equivalently to an
            administrator's password. Your API Key should never be shared in
            public spaces. You should take extra precaution to ensure that your
            API keys are never committed to your public git repositories.
          </p>
          <p>
            On your administrator dashboard, you'll be able to generate API keys
            for both testing and production use. You can revoke old keys and
            generate new ones at any time.
          </p>
          <p>
            For every request made to the API, you should include the API Key in
            the header of your HTTPS request under "Authorization". Any request
            made without a valid Authorization header will return a{" "}
            <code>403</code> error response. See an example using{" "}
            <code>curl</code> on the right. Furthermore, all API requests must be
            made over HTTPS.
          </p>
          <p>
            Every GraphQL request is replied with by a valid JSON string that
            represents the data being fetched or any errors incurred in the
            fetching (e.g. if you are unauthorized to access the data being
            requested).
          </p>
          <h2>Getting Started with Your First Query</h2>
          <p>
            The easiest way to get started is by querying your organization information.
            This simple query will help you verify that your API key is working correctly
            and give you basic information about your Vetspire organization.
          </p>
          <p>
            All you need to do is make a POST request to the API endpoint with your
            query and include your API key in the Authorization header.
          </p>

          <h2>Limits</h2>
          <p>
            We currently enforce a maximum query depth of 8. If you exceed this,
            you'll see the an error code <code>query_depth_limit</code> with a
            message: Query has depth of 9, which exceeds max depth of 8. This
            limit helps us ensure we can provide a reliable service to all API
            users.
          </p>
          <p>
            If you run into this error, please split up your query into multiple
            smaller queries or reconsider whether you need all the information you
            requested.
          </p>
        </div>
        <div className="section-example on">
          <div className="section-example-area">
            <CodeExample
              title="Your First GraphQL Query"
              code={`query GetOrganization {
  org {
    id
    name
  }
}`}
              language="graphql"
            />
          </div>
          <div className="section-example-area">
            <CodeExample
              title="cURL Request"
              code={`curl https://api.staging.vetspire.com/graphql \\
  -X POST \\
  -H "Authorization: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "query GetOrganization { org { id name } }"}'`}
              language="bash"
            />
          </div>
          <div className="section-example-area">
            <CodeExample
              title="Example Response"
              code={`{
  "data": {
    "org": {
      "id": "1",
      "name": "Your Clinic Name"
    }
  }
}`}
              language="json"
            />
          </div>
        </div>
      </section>
      <section>
        <div className="section-text">
          <h2>Exploring the API Reference</h2>
          <p>
            Our comprehensive API reference is organized by veterinary practice domains,
            making it easy to find the functionality you need. Use the sidebar navigation
            to explore different areas of the API.
          </p>

          <div style={{
            display: "flex",
            gap: "24px",
            marginTop: "24px",
            marginBottom: "24px",
            alignItems: "flex-start"
          }}>
            <div style={{
              flex: 1,
              backgroundColor: darkMode ? "#161b22" : "#ffffff",
              border: `1px solid ${darkMode ? "#30363d" : "#e2e8f0"}`,
              borderRadius: "8px",
              padding: "20px",
              borderTop: "3px solid #10b981"
            }}>
              <h4 style={{ color: "#10b981", margin: "0 0 12px 0", display: "flex", alignItems: "center", gap: "8px" }}>
                <MagnifyingGlassIcon /> <span>Queries</span>
              </h4>
              <p style={{ margin: "0 0 8px 0", fontSize: "1.4rem" }}>
                Fetch data from your practice management system:
              </p>
              <ul style={{ fontSize: "1.3rem", color: "#718096", paddingLeft: "20px" }}>
                <li>Client and patient information</li>
                <li>Appointment schedules</li>
                <li>Medical records and encounters</li>
                <li>Billing and invoices</li>
                <li>Inventory management</li>
              </ul>
            </div>

            <div style={{
              flex: 1,
              backgroundColor: darkMode ? "#161b22" : "#ffffff",
              border: `1px solid ${darkMode ? "#30363d" : "#e2e8f0"}`,
              borderRadius: "8px",
              padding: "20px",
              borderTop: "3px solid #f59e0b"
            }}>
              <h4 style={{ color: "#f59e0b", margin: "0 0 12px 0", display: "flex", alignItems: "center", gap: "8px" }}>
                <PencilSquareIcon /> <span>Mutations</span>
              </h4>
              <p style={{ margin: "0 0 8px 0", fontSize: "1.4rem" }}>
                Create, update, and manage practice data:
              </p>
              <ul style={{ fontSize: "1.3rem", color: "#718096", paddingLeft: "20px" }}>
                <li>Schedule appointments</li>
                <li>Update patient records</li>
                <li>Process payments</li>
                <li>Manage treatments</li>
                <li>Handle communications</li>
              </ul>
            </div>
          </div>

          <div className="pro-tips-section" style={{
            backgroundColor: darkMode ? "#0d1117" : "#f0f9ff",
            border: `1px solid ${darkMode ? "#1f6feb" : "#0ea5e9"}`,
            borderRadius: "8px",
            padding: "20px",
            marginTop: "24px"
          }}>
            <h4 style={{ margin: "0 0 12px 0", display: "flex", alignItems: "center", gap: "8px" }}>
              <LightBulbIcon /> <span>Pro Tips for Navigation</span>
            </h4>
            <ul style={{ margin: "0", paddingLeft: "20px" }}>
              <li><strong>Use the search (⌘K):</strong> Quickly find queries, mutations, or types</li>
              <li><strong>Organized by domain:</strong> Related operations are grouped together (e.g., Clinical, Billing)</li>
              <li><strong>Detailed descriptions:</strong> Each field includes context-aware descriptions</li>
              <li><strong>Type system:</strong> Explore object types, enums, and input objects in the sidebar</li>
            </ul>
          </div>

          <h3>Understanding GraphQL Syntax</h3>
          <p>
            Each operation in our API follows GraphQL conventions. Here's how to read
            the reference documentation:
          </p>

          <div className="graphql-syntax-section" style={{
            backgroundColor: darkMode ? "#21262d" : "#fafafa",
            border: `1px solid ${darkMode ? "#30363d" : "#e5e7eb"}`,
            borderRadius: "8px",
            padding: "20px",
            marginTop: "16px"
          }}>
            <h4 style={{ margin: "0 0 12px 0", fontFamily: "monospace" }}>
              getClients(active: Boolean, limit: Int!): [Client]
            </h4>
            <div style={{ fontSize: "1.4rem" }}>
              <p style={{ margin: "0 0 8px 0" }}>
                <strong>getClients</strong> - The operation name
              </p>
              <p style={{ margin: "0 0 8px 0" }}>
                <strong>active: Boolean</strong> - Optional parameter (no exclamation mark)
              </p>
              <p style={{ margin: "0 0 8px 0" }}>
                <strong>limit: Int!</strong> - Required parameter (exclamation mark)
              </p>
              <p style={{ margin: "0" }}>
                <strong>[Client]</strong> - Returns an array of Client objects
              </p>
            </div>
          </div>
        </div>
        <div className="section-example on">
          <div style={{
            padding: "20px",
            backgroundColor: darkMode ? "#0d1117" : "#1a202c",
            borderRadius: "6px",
            fontSize: "1.4rem",
            lineHeight: "1.6",
            color: "#e2e8f0"
          }}>
            <p style={{ margin: "0 0 16px 0", fontWeight: "600", color: "#63b3ed" }}>GraphQL Type Notation:</p>
            <p style={{ margin: "0 0 8px 0" }}>
              <code style={{
                backgroundColor: darkMode ? "#21262d" : "#2d3748",
                color: "#68d391",
                padding: "4px 8px",
                borderRadius: "4px",
                fontWeight: "600"
              }}>
                String
              </code> - A text value
            </p>
            <p style={{ margin: "0 0 8px 0" }}>
              <code style={{
                backgroundColor: darkMode ? "#21262d" : "#2d3748",
                color: "#f56565",
                padding: "4px 8px",
                borderRadius: "4px",
                fontWeight: "600"
              }}>
                String!
              </code> - A required text value
            </p>
            <p style={{ margin: "0 0 8px 0" }}>
              <code style={{
                backgroundColor: darkMode ? "#21262d" : "#2d3748",
                color: "#9f7aea",
                padding: "4px 8px",
                borderRadius: "4px",
                fontWeight: "600"
              }}>
                [String]
              </code> - An array of text values
            </p>
            <p style={{ margin: "0" }}>
              <code style={{
                backgroundColor: darkMode ? "#21262d" : "#2d3748",
                color: "#ed8936",
                padding: "4px 8px",
                borderRadius: "4px",
                fontWeight: "600"
              }}>
                [String!]!
              </code> - A required array of required text values
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndexPage;



// WEBPACK FOOTER //
// ./src/pages/index.js